import { useState, useRef } from "react";
import useFocoModal from "../../hooks/useFocoModal";
import { operadoresDe, operador, agrupables, familia } from "./notionFilters";
import Campo from "../forms/Campo";

// ── Editor de una vista ("pantalla") de Notion ───────────────────────────────
// Define QUÉ se ve y CÓMO: tipo de vista, agrupación del tablero, columnas de la
// tabla, filtros sobre cualquier propiedad y orden. Lo que sale de aquí es el
// objeto `config` que se guarda en el servidor tal cual.

const inS = {
  width: "100%", padding: "7px 10px", borderRadius: 8, fontSize: 12.5,
  border: "1px solid var(--border-ctl)", background: "var(--surface2)",
  color: "var(--text)", fontFamily: "var(--font)", boxSizing: "border-box",
};
const lbS = {
  fontSize: 11, color: "var(--text3)", fontWeight: 700, display: "block",
  marginBottom: 5, textTransform: "uppercase", letterSpacing: ".06em",
};
const btnS = {
  fontSize: 12.5, fontWeight: 600, padding: "7px 14px", borderRadius: 8,
  border: "1px solid var(--border)", background: "var(--surface)",
  color: "var(--text2)", cursor: "pointer", fontFamily: "var(--font)",
};

export const VISTA_POR_DEFECTO = {
  tipo: "tablero",        // "tablero" | "tabla"
  agruparPor: null,       // nombre de propiedad (tablero)
  columnas: [],           // nombres de propiedad (tabla)
  filtros: [],            // [{ prop, op, value }]
  modo: "todos",          // "todos" (AND) | "alguno" (OR)
  ordenProp: null,        // "__title" o nombre de propiedad
  ordenDir: "asc",
};

// Valores propuestos para un filtro de tipo "opciones", según la propiedad
function opcionesDe(def, { users = [], pages = [], relationTitles = {} }) {
  if (!def) return [];
  if (def.options?.length) return def.options.map(o => o.name);
  if (def.type === "people") {
    // Los del workspace, más los que aparezcan en los datos (por si alguien ya
    // no está en el workspace pero sigue asignado en páginas antiguas).
    const enDatos = pages.flatMap(p => (p.properties?.[def.name]?.value || []).map(v => v?.name).filter(Boolean));
    return [...new Set([...users.map(u => u.name), ...enDatos])];
  }
  if (def.type === "relation") {
    const ids = [...new Set(pages.flatMap(p => p.properties?.[def.name]?.value || []))];
    return ids.map(id => relationTitles[id] || id);
  }
  // Cualquier otra: lo que realmente haya en los datos
  return [...new Set(pages.flatMap(p => {
    const v = p.properties?.[def.name]?.value;
    if (v === null || v === undefined || v === "") return [];
    return Array.isArray(v) ? v.map(x => (typeof x === "object" ? x.name : x)).filter(Boolean) : [String(v)];
  }))].slice(0, 100);
}

// ── Una condición de filtro ───────────────────────────────────────────────────
function FilaFiltro({ filtro, schema, ctx, onChange, onRemove }) {
  const def  = schema.find(p => p.name === filtro.prop);
  const ops  = operadoresDe(def?.type || "rich_text");
  const meta = operador(def?.type || "rich_text", filtro.op);
  const opciones = meta.valor === "opciones" ? opcionesDe(def, ctx) : [];
  const seleccion = Array.isArray(filtro.value) ? filtro.value : filtro.value ? [filtro.value] : [];

  const cambiarProp = (name) => {
    const nuevoDef = schema.find(p => p.name === name);
    onChange({ prop: name, op: operadoresDe(nuevoDef?.type)[0].op, value: null });
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1.2fr auto", gap: 6, alignItems: "start" }}>
      <select aria-label="Propiedad del filtro" style={inS} value={filtro.prop} onChange={e => cambiarProp(e.target.value)}>
        {schema.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
      </select>

      <select aria-label="Condición del filtro" style={inS} value={filtro.op} onChange={e => onChange({ ...filtro, op: e.target.value, value: null })}>
        {ops.map(o => <option key={o.op} value={o.op}>{o.label}</option>)}
      </select>

      {meta.valor === null ? (
        <span style={{ fontSize: 11.5, color: "var(--text3)", paddingTop: 8 }}>—</span>
      ) : meta.valor === "opciones" ? (
        <select aria-label="Valores del filtro" style={{ ...inS, minHeight: 62 }} multiple value={seleccion}
          onChange={e => onChange({ ...filtro, value: [...e.target.selectedOptions].map(o => o.value) })}>
          {opciones.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input aria-label="Valor del filtro" style={inS}
          type={meta.valor === "fecha" ? "date" : meta.valor === "numero" ? "number" : "text"}
          value={filtro.value ?? ""}
          placeholder={meta.valor === "numero" ? "0" : ""}
          onChange={e => onChange({ ...filtro, value: e.target.value })} />
      )}

      <button onClick={onRemove} title="Quitar condición"
        style={{ ...btnS, padding: "7px 10px", color: "#dc2626", borderColor: "#fca5a5" }}>✕</button>
    </div>
  );
}

export default function NotionViewEditor({ vista, schema = [], ctx = {}, onSave, onSaveAsNew, onDelete, onClose }) {
  const cajaRef = useRef(null);
  useFocoModal(cajaRef, true, onClose);
  const [name, setName]     = useState(vista?.name || "Nueva vista");
  const [config, setConfig] = useState({ ...VISTA_POR_DEFECTO, ...(vista?.config || {}) });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setConfig(c => ({ ...c, [k]: v }));
  const propsFiltrables = schema.filter(p => p.type !== "title");
  const propsAgrupables = agrupables(schema);
  const ordenables = [{ name: "__title", label: "Título" },
                      ...schema.filter(p => p.type !== "title").map(p => ({ name: p.name, label: p.name }))];

  const addFiltro = () => {
    const def = propsFiltrables[0] || schema[0];
    if (!def) return;
    set("filtros", [...(config.filtros || []), { prop: def.name, op: operadoresDe(def.type)[0].op, value: null }]);
  };

  const guardar = async (comoNueva = false) => {
    setSaving(true);
    try { await (comoNueva ? onSaveAsNew : onSave)({ name, config }); } finally { setSaving(false); }
  };

  const toggleCol = (name_) => {
    const cols = config.columnas || [];
    set("columnas", cols.includes(name_) ? cols.filter(c => c !== name_) : [...cols, name_]);
  };

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 320, background: "rgba(0,0,0,0.18)" }} />
      <div ref={cajaRef} role="dialog" aria-modal="true" aria-label="Editar vista" style={{
        position: "fixed", top: 0, right: 0, bottom: 0, zIndex: 321,
        width: "min(620px, 96vw)", background: "var(--surface)",
        borderLeft: "1px solid var(--border)", boxShadow: "-4px 0 24px rgba(0,0,0,0.12)",
        display: "flex", flexDirection: "column",
      }}>
        {/* Cabecera */}
        <div style={{ padding: "16px 20px 12px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ flex: 1 }}>
            <Campo><label style={lbS}>Nombre de la vista</label>
            <input style={{ ...inS, fontWeight: 700, fontSize: 14 }} value={name}
              onChange={e => setName(e.target.value)} placeholder="Proyectos activos de TIC" /></Campo>
          </div>
          <button onClick={onClose} style={{ ...btnS, border: "none", background: "transparent", fontSize: 16, color: "var(--text3)" }}>✕</button>
        </div>

        {/* Cuerpo */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Tipo de vista */}
          <div>
            <Campo><label style={lbS}>Cómo se ve</label>
            <div style={{ display: "flex", gap: 6 }}>
              {[["tablero", "Tablero"], ["tabla", "Tabla"]].map(([v, l]) => (
                <button key={v} onClick={() => set("tipo", v)} style={{
                  ...btnS,
                  background: config.tipo === v ? "var(--accent-bg)" : "var(--surface)",
                  borderColor: config.tipo === v ? "var(--accent)" : "var(--border-ctl)",
                  color: config.tipo === v ? "var(--accent)" : "var(--text2)",
                }}>{l}</button>
              ))}
            </div></Campo>
          </div>

          {/* Agrupación (tablero) */}
          {config.tipo === "tablero" && (
            <div>
              <label style={lbS} htmlFor="nve-agrupar">Agrupar las columnas por</label>
              {propsAgrupables.length === 0 ? (
                <div style={{ fontSize: 12.5, color: "var(--text3)" }}>
                  Esta base de datos no tiene ninguna propiedad de tipo estado, selección, casilla, multi-selección o persona, así que no hay por dónde agrupar. Usa la vista de tabla.
                </div>
              ) : (
                <select id="nve-agrupar" style={inS} value={config.agruparPor || ""} onChange={e => set("agruparPor", e.target.value || null)}>
                  <option value="">— elige una propiedad —</option>
                  {propsAgrupables.map(p => <option key={p.name} value={p.name}>{p.name} ({p.type})</option>)}
                </select>
              )}
              <div style={{ fontSize: 11.5, color: "var(--text3)", marginTop: 6 }}>
                Solo se puede cambiar arrastrando cuando se agrupa por estado o selección: con multi-selección o personas, mover una tarjeta sería ambiguo.
              </div>
            </div>
          )}

          {/* Columnas (tabla) */}
          {config.tipo === "tabla" && (
            <div>
              <Campo><label style={lbS}>Columnas visibles</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {propsFiltrables.map(p => {
                  const on = (config.columnas || []).includes(p.name);
                  return (
                    <button key={p.name} onClick={() => toggleCol(p.name)} title={p.type} style={{
                      ...btnS, padding: "5px 11px", fontSize: 11.5,
                      background: on ? "var(--accent-bg)" : "var(--surface)",
                      borderColor: on ? "var(--accent)" : "var(--border-ctl)",
                      color: on ? "var(--accent)" : "var(--text2)",
                    }}>{on ? "✓ " : ""}{p.name}</button>
                  );
                })}
              </div></Campo>
            </div>
          )}

          {/* Filtros */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <Campo><label style={{ ...lbS, marginBottom: 0 }}>Filtros</label>
              <div style={{ flex: 1 }} /></Campo>
              {(config.filtros || []).length > 1 && (
                <select aria-label="Cuántos filtros se cumplen" style={{ ...inS, width: "auto", padding: "4px 8px", fontSize: 11.5 }}
                  value={config.modo} onChange={e => set("modo", e.target.value)}>
                  <option value="todos">Cumplir todas</option>
                  <option value="alguno">Cumplir alguna</option>
                </select>
              )}
              <button onClick={addFiltro} style={{ ...btnS, padding: "5px 11px", fontSize: 11.5 }}>+ Condición</button>
            </div>
            {(config.filtros || []).length === 0 ? (
              <div style={{ fontSize: 12.5, color: "var(--text3)" }}>Sin filtros: se ven todas las páginas de la base de datos.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {config.filtros.map((f, i) => (
                  <FilaFiltro key={i} filtro={f} schema={propsFiltrables} ctx={ctx}
                    onChange={nf => set("filtros", config.filtros.map((x, j) => (j === i ? nf : x)))}
                    onRemove={() => set("filtros", config.filtros.filter((_, j) => j !== i))} />
                ))}
              </div>
            )}
          </div>

          {/* Orden */}
          <div>
            <Campo><label style={lbS}>Ordenar por</label>
            <div style={{ display: "flex", gap: 6 }}>
              <select style={inS} value={config.ordenProp || ""} onChange={e => set("ordenProp", e.target.value || null)}>
                <option value="">Orden de Notion</option>
                {ordenables.map(o => <option key={o.name} value={o.name}>{o.label}</option>)}
              </select>
              <select style={{ ...inS, width: 130 }} value={config.ordenDir} onChange={e => set("ordenDir", e.target.value)} disabled={!config.ordenProp}>
                <option value="asc">Ascendente</option>
                <option value="desc">Descendente</option>
              </select>
            </div></Campo>
          </div>

          {/* Resumen de tipos, para que se vea con qué se está trabajando */}
          <div style={{ fontSize: 11.5, color: "var(--text3)", borderTop: "1px solid var(--border)", paddingTop: 12 }}>
            {schema.length} propiedades en esta base de datos:{" "}
            {[...new Set(schema.map(p => familia(p.type)))].join(", ")}.
          </div>
        </div>

        {/* Pie */}
        <div style={{ padding: "12px 20px", borderTop: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 10 }}>
          {vista?.id && (
            <button onClick={onDelete} disabled={saving}
              style={{ ...btnS, color: "#dc2626", borderColor: "#fca5a5" }}>Eliminar vista</button>
          )}
          <div style={{ flex: 1 }} />
          {onSaveAsNew && (
            <button onClick={() => guardar(true)} disabled={saving || !name.trim()} style={btnS}
              title="Deja intacta esta vista y crea otra con esta configuración">
              Guardar como nueva
            </button>
          )}
          <button onClick={onClose} disabled={saving} style={btnS}>Cancelar</button>
          <button onClick={() => guardar(false)} disabled={saving || !name.trim()} style={{
            ...btnS, border: "none", fontWeight: 700,
            background: "var(--accent)", color: "var(--on-accent)",
            opacity: saving || !name.trim() ? 0.6 : 1,
          }}>{saving ? "Guardando…" : vista?.id ? "Guardar cambios" : "Crear vista"}</button>
        </div>
      </div>
    </>
  );
}
